import type { RankId } from '@kolony/shared';
import { rankArtUrl, rankBandLabel, RANK_BLURBS, rankName } from './rankInfo';

// The promotion (H14): the shield arrives blurred and dark, the blur lifts on the fanfare,
// and the name lands as a light sweep crosses it.
//
// It is its OWN layer, deliberately not a member of the modal group — the same reasoning
// `breakMenu.ts` records. A promotion can arrive while the non-dismissible bandit discard is
// open, and one-modal-at-a-time would shoulder the discard aside, which is D68 exactly.
// Its z-index sits above the ordinary modals and below the discard's host.
//
// Timing is not free-form: `rank-up` is front-loaded (a −5.8 dB peak inside its first half
// second), so the reveal is scheduled to land on the attack rather than after it.

const AUTO_DISMISS_MS = 6000;

export interface PromotionOverlayOpts {
  /** Plays a cue id through the game's audio service; absent ⇒ silent. */
  play?: (cue: 'rank-up' | 'rank-bell') => void;
  reducedMotion?: () => boolean;
}

export interface PromotionOverlay {
  show(rank: RankId, awarded: boolean): void;
  hide(): void;
  readonly visible: boolean;
  dispose(): void;
}

export function mountPromotionOverlay(host: HTMLElement, opts: PromotionOverlayOpts = {}): PromotionOverlay {
  const doc = host.ownerDocument;
  const root = doc.createElement('div');
  root.className = 'promo-host';
  root.dataset['promoHost'] = '1';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-live', 'polite');
  root.hidden = true;

  const inner = doc.createElement('div');
  inner.className = 'promo-inner';
  const rays = doc.createElement('div');
  rays.className = 'promo-rays';
  const halo = doc.createElement('div');
  halo.className = 'promo-halo';
  const shieldWrap = doc.createElement('div');
  shieldWrap.className = 'promo-shield';
  const shield = doc.createElement('img');
  shield.alt = '';
  shield.decoding = 'async';
  shieldWrap.appendChild(shield);
  inner.append(rays, halo, shieldWrap);

  const text = doc.createElement('div');
  text.className = 'promo-text';
  const kicker = doc.createElement('p');
  kicker.className = 'promo-kicker';
  const name = doc.createElement('p');
  name.className = 'promo-name';
  const band = doc.createElement('p');
  band.className = 'promo-band';
  const blurb = doc.createElement('p');
  blurb.className = 'promo-blurb';
  const cont = doc.createElement('p');
  cont.className = 'promo-cont';
  cont.textContent = 'Tap to continue';
  text.append(kicker, name, band, blurb, cont);

  root.append(inner, text);
  host.appendChild(root);

  let timer: ReturnType<typeof setTimeout> | null = null;
  let visible = false;

  function hide(): void {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    visible = false;
    root.hidden = true;
    root.classList.remove('promo-host--on');
  }

  function show(rank: RankId, awarded: boolean): void {
    // Cancel the outgoing promotion's dismissal FIRST. Without this a second promotion
    // inherits the first one's countdown and is cut short — which is exactly what happens
    // when a settle drains two games at once, or placement completes and a floor is crossed
    // in the same breath.
    if (timer !== null) clearTimeout(timer);
    shield.src = rankArtUrl(rank);
    // The first rank is AWARDED — the end of a countdown the player has been watching.
    // Every one after it is ATTAINED. Same sequence, one word apart.
    kicker.textContent = awarded ? 'Rank awarded' : 'Rank attained';
    name.textContent = rankName(rank);
    band.textContent = rankBandLabel(rank);
    blurb.textContent = RANK_BLURBS[rank];

    root.hidden = false;
    root.classList.remove('promo-host--on');
    void root.offsetWidth; // restart the animation when one promotion follows another
    root.classList.add('promo-host--on');
    visible = true;

    if (opts.reducedMotion?.() !== true) {
      opts.play?.('rank-bell');
      setTimeout(() => opts.play?.('rank-up'), 260);
    }
    timer = setTimeout(hide, AUTO_DISMISS_MS);
  }

  const onClick = (): void => hide();
  const onKey = (e: KeyboardEvent): void => {
    if (visible && e.key === 'Escape') hide();
  };
  root.addEventListener('click', onClick);
  doc.addEventListener('keydown', onKey);

  return {
    show,
    hide,
    get visible() {
      return visible;
    },
    dispose(): void {
      hide();
      root.removeEventListener('click', onClick);
      doc.removeEventListener('keydown', onKey);
      root.remove();
    },
  };
}
